import type { Catalogue } from './types';

/**
 * The words for invitations, the account screen, password recovery and notification channels, shared by
 * all four apps and merged into `commonStrings`.
 *
 * Every message is one string with `{placeholders}`, never a sentence built from fragments. Phone numbers
 * are passed in already formatted, which wraps them in a left-to-right mark so the `+` stays on the correct
 * end inside Arabic text.
 */
export const accountStrings: Catalogue = {
  ar: {
    // Sign-in links
    'auth.forgotPassword': 'نسيت كلمة السر؟',
    'auth.haveInvitation': 'لديّ دعوة',
    'auth.backToSignIn': 'العودة إلى تسجيل الدخول',
    'legal.title': 'الخصوصية والشروط',
    'legal.privacy': 'إشعار الخصوصية',
    'legal.terms': 'شروط الاستخدام',
    'legal.read.privacy': 'اقرأ إشعار الخصوصية',
    'legal.read.terms': 'اقرأ شروط الاستخدام',
    'legal.consent': 'قرأت إشعار الخصوصية وشروط الاستخدام، وأوافق عليهما.',
    'legal.consent.required': 'ضع علامة الموافقة على إشعار الخصوصية وشروط الاستخدام لتتابع.',
    'legal.version': 'النسخة {version}',
    'legal.updated.title': 'نُشرت نسخة جديدة من الشروط',
    'legal.updated.body': 'اقرأ إشعار الخصوصية وشروط الاستخدام، ثم وافق عليهما لتتابع استعمال التطبيق.',
    'legal.accept': 'أوافق وأتابع',
    'auth.entry.otherNursery.title': 'هذا رابط {nursery}',
    'auth.entry.otherNursery.body': 'أنت داخل الآن إلى {current}. لتدخل إلى {nursery}، سجّل الخروج ثم ادخل من جديد.',
    'auth.entry.signOutAndContinue': 'سجّل الخروج وتابع',
    'auth.entry.stay': 'ابقَ في {current}',

    // Accepting an invitation
    'auth.invitation.paste.label': 'رابط الدعوة',
    'auth.invitation.paste.hint': 'الصق الرابط الذي وصلك برسالة نصية.',
    'auth.invitation.paste.invalid': 'هذا ليس رابط دعوة. انسخ الرابط كاملاً من الرسالة النصية.',
    'auth.invitation.continue': 'متابعة',
    'auth.invitation.for': 'دعوة للانضمام إلى {place} باسم {name}.',
    'auth.invitation.forPlace': 'دعوة للانضمام إلى {place}.',
    'auth.invitation.platformTeam': 'فريق دغفل',
    'auth.invitation.expires': 'تنتهي صلاحية الدعوة في {date}.',
    'auth.invitation.codeTo': 'سنرسل رمز تأكيد إلى الرقم {phone}.',
    'auth.invitation.existingAccount': 'لهذا الرقم حساب على دغفل. أدخل كلمة سر الحساب لتنضم به، ولا حاجة إلى كلمة سر جديدة.',
    'auth.invitation.currentPassword': 'كلمة سر حسابك',
    'auth.invitation.notYours': 'هذا ليس حسابي',
    'auth.invitation.mine': 'هذا حسابي',
    'auth.invitation.release.self': 'سنفتح لك حساباً جديداً بهذا الرقم. يبقى الحساب القديم بسجلاته لصاحبه، ولا يستطيع أحد الدخول إليه بعد اليوم. اختر كلمة سر لحسابك الجديد.',
    'auth.invitation.release.submit': 'افتح حسابي الجديد',
    'auth.invitation.release.centre': 'استُخدم الحساب المرتبط بهذا الرقم مؤخراً، فلا يُفصل عنه برسالة نصية وحدها. راجع إدارة الجهة التي دعتك لتتأكد منك شخصياً وتفصل الحساب القديم، ثم افتح الدعوة من جديد.',
    'auth.invitation.release.platform': 'لا يُفصل الحساب المرتبط بهذا الرقم برسالة نصية وحدها. اطلب من الجهة التي دعتك أن تتواصل مع فريق دغفل ليتأكد منك ويفصل الحساب القديم، ثم افتح الدعوة من جديد.',
    'auth.code': 'رمز التأكيد',
    'auth.code.hint': 'ستة أرقام تصلك برسالة نصية.',
    'auth.code.required': 'أدخل الرمز المكوّن من ستة أرقام.',
    'auth.code.send': 'أرسل الرمز',
    'auth.code.sent': 'أرسلنا الرمز. قد يستغرق وصوله دقيقة.',
    'auth.code.failed': 'تعذّر إرسال الرسالة الآن. حاول بعد قليل، أو تواصل مع إدارة الحضانة.',
    'auth.code.resendIn.zero': 'يمكنك طلب رمز جديد الآن',
    'auth.code.resendIn.one': 'يمكنك طلب رمز جديد بعد ثانية واحدة',
    'auth.code.resendIn.two': 'يمكنك طلب رمز جديد بعد ثانيتين',
    'auth.code.resendIn.few': 'يمكنك طلب رمز جديد بعد {count} ثوانٍ',
    'auth.code.resendIn.many': 'يمكنك طلب رمز جديد بعد {count} ثانية',
    'auth.code.resendIn.other': 'يمكنك طلب رمز جديد بعد {count} ثانية',
    'auth.code.resendInMinutes.two': 'يمكنك طلب رمز جديد بعد دقيقتين',
    'auth.code.resendInMinutes.few': 'يمكنك طلب رمز جديد بعد {count} دقائق',
    'auth.code.resendInMinutes.other': 'يمكنك طلب رمز جديد بعد {count} دقيقة',

    // Forgotten password
    'auth.reset.title': 'استعادة كلمة السر',
    'auth.reset.hint': 'أدخل رقم هاتفك الذي تسجّل به الدخول، وسنرسل إليه رمزاً.',
    'auth.reset.sent': 'إن كان الرقم {phone} مسجّلاً فسيصله رمز خلال دقيقة.',
    'auth.reset.newPassword': 'كلمة السر الجديدة',
    'auth.reset.confirmPassword': 'تأكيد كلمة السر الجديدة',
    'auth.reset.submit': 'احفظ كلمة السر',
    'auth.reset.done': 'تغيّرت كلمة السر وخرجتَ من كل الأجهزة. سجّل الدخول بها الآن.',
    'auth.reset.held': 'لحماية حسابك لن تتغيّر كلمة السر الآن. يمكنك إكمالها بعد {date}: افتح هذه الشاشة من جديد واطلب رمزاً جديداً. إن كان حسابك مفتوحاً على جهاز آخر فستظهر عليه رسالة بهذا الطلب.',
    'auth.reset.awaitingCentre': 'لن تتغيّر كلمة السر حتى تتأكد إدارة الحضانة منك شخصياً. راجع الإدارة، ثم افتح هذه الشاشة من جديد واطلب رمزاً جديداً.',
    'auth.reset.awaitingPlatform': 'لن تتغيّر كلمة السر حتى يتأكد فريق دغفل منك. اطلب من إدارة الحضانة أن تتواصل معه، ثم افتح هذه الشاشة من جديد واطلب رمزاً جديداً.',
    'recovery.banner': 'طلب أحدهم إعادة تعيين كلمة السر لحسابك برمز وصل إلى رقمك. إن لم تكن أنت فألغِ الطلب الآن.',
    'recovery.cancel': 'لست أنا، ألغِ الطلب',
    'auth.recoverLink.title': 'تعيين كلمة سر جديدة',
    'auth.recoverLink.hint': 'وصلك هذا الرابط من فريق دغفل. يعمل مرة واحدة فقط.',
    'auth.recoverLink.submit': 'احفظ كلمة السر',
    'auth.recoverLink.done': 'حُفظت كلمة السر الجديدة. سجّل الدخول بها الآن. إن كنت من فريق دغفل فأضف مفتاح دخول قبل أن تفتح لوحة المنصّة.',
    'auth.recoverLink.missing': 'هذا الرابط ناقص. افتح الرابط كاملاً كما وصلك من فريق دغفل.',
    'account.passkeys.currentPassword': 'كلمة السر للتأكيد',
    'account.passkeys.currentPasswordHint': 'لحماية حسابك نطلب كلمة السر قبل إضافة مفتاح دخول.',

    // Confirming a new phone number
    'account.phoneConfirm.title': 'تأكيد الرقم الجديد',
    'account.phoneConfirm.hint': 'اضغط الزر لاعتماد هذا الرقم لحسابك. ستخرج بعدها من كل الأجهزة.',
    'account.phoneConfirm.submit': 'اعتمد هذا الرقم',
    'account.phoneConfirm.done': 'أصبح رقمك {phone}. سجّل الدخول بالرقم الجديد.',
    'account.phoneConfirm.missing': 'الرابط غير مكتمل. افتحه كاملاً من الرسالة النصية.',

    // Profile photos, for people and for children
    'account.photo': 'الصورة الشخصية',
    'account.photo.hint': 'تظهر صورتك بجانب اسمك في كل تطبيقات دغفل.',
    'child.photo': 'صورة الطفل',
    'child.photo.hint': 'تساعد الفريق على التعرّف على الطفل عند الوصول والاستلام، ولا تظهر لأي عائلة أخرى.',
    'photo.add': 'اختيار صورة',
    'photo.change': 'تغيير الصورة',
    'photo.remove': 'إزالة الصورة',
    'photo.save': 'حفظ الصورة',
    'photo.preview': 'هكذا ستظهر الصورة. احفظها أو اختر غيرها.',
    'photo.saved': 'حُفظت الصورة.',
    'photo.removed': 'أُزيلت الصورة.',
    'photo.unreadable': 'تعذّرت قراءة هذا الملف كصورة. اختر صورة بصيغة JPEG أو PNG.',
    'photo.of': 'صورة {name}',

    // A nursery's logo, set by its own administrators or by the platform
    'logo.title': 'شعار الحضانة',
    'logo.hint': 'يظهر في شاشة تسجيل الدخول وعند اختيار الحضانة. يراه كل من يعرف عنوان الحضانة.',
    'logo.add': 'اختيار شعار',
    'logo.change': 'تغيير الشعار',
    'logo.remove': 'إزالة الشعار',
    'logo.save': 'حفظ الشعار',
    'logo.preview': 'هكذا سيظهر الشعار. احفظه أو اختر غيره.',
    'logo.saved': 'حُفظ الشعار.',
    'logo.removed': 'أُزيل الشعار.',
    'logo.unreadable': 'تعذّرت قراءة هذا الملف كصورة. اختر شعاراً بصيغة PNG أو JPEG.',

    // The account screen
    'account.title': 'حسابي',
    'account.profile': 'البيانات الشخصية',
    'account.name': 'الاسم',
    'account.name.hint': 'تغيّره إدارة الحضانة عند الحاجة.',
    'account.phone': 'رقم الهاتف',
    'account.phone.hint': 'هو رقم تسجيل الدخول.',
    'account.phone.unconfirmed': 'غير مؤكّد',
    'account.phone.new': 'الرقم الجديد',
    'account.phone.send': 'أرسل رابط التأكيد',
    'account.phone.sent': 'أرسلنا رابطاً إلى {phone}. افتحه خلال ساعة لإتمام التغيير. إن لم يصل خلال دقائق فتحقّق من الرقم.',
    'account.phone.pending': 'تغيير الرقم إلى {phone} ينتظر فتح الرابط حتى {time}. يبقى رقمك الحالي فعّالاً حتى ذلك الحين.',
    'account.phone.cancel': 'ألغِ تغيير الرقم',
    'account.email': 'البريد الإلكتروني',
    'account.email.none': 'لا يوجد',
    'account.email.hint': 'اختياري، للفواتير والإشعارات العادية فقط. لا يُستخدم لتسجيل الدخول.',
    'account.email.new': 'البريد الإلكتروني',
    'account.email.save': 'احفظ البريد',
    'account.email.remove': 'أزِل البريد',
    'account.email.saved': 'حُفظ البريد الإلكتروني.',
    'account.email.removed': 'أُزيل البريد الإلكتروني.',
    'account.change': 'تغيير',
    'account.currentPassword': 'كلمة السر الحالية',
    'account.currentPassword.hint': 'نطلبها قبل أي تغيير، حتى لا يغيّر أحد حسابك من جهاز تركته مفتوحاً.',
    'account.password': 'كلمة السر',
    'account.password.new': 'كلمة السر الجديدة',
    'account.password.confirm': 'تأكيد كلمة السر الجديدة',
    'account.password.save': 'غيّر كلمة السر',
    'account.password.saved': 'تغيّرت كلمة السر، وخرجتَ من كل الأجهزة الأخرى.',
    'account.password.mismatch': 'كلمتا السر الجديدتان غير متطابقتين.',
    'account.password.reset.hint': 'نرسل رمزاً إلى رقم حسابك، وبه تختار كلمة سر جديدة دون الحاجة إلى القديمة.',
    'account.password.reset.sent': 'أرسلنا رمزاً إلى الرقم {phone}. قد يستغرق وصوله دقيقة.',
    'account.password.reset.submit': 'احفظ كلمة السر الجديدة',
    'account.sessions': 'الأجهزة المتصلة',
    'account.sessions.hint': 'كل جهاز سجّلتَ الدخول منه ولم تخرج.',
    'account.sessions.since': 'منذ {date}',
    'account.sessions.unknownDevice': 'جهاز غير معروف',
    'account.sessions.signOutAll': 'اخرج من كل الأجهزة',
    'account.notifications': 'قنوات الإشعارات',

    // Passkeys
    'auth.passkey.or': 'أو',
    'auth.passkey.signIn': 'الدخول بمفتاح المرور',
    'auth.passkey.signIn.hint': 'ببصمة الإصبع أو الوجه أو قفل شاشة الجهاز، دون كلمة سر.',
    'auth.passkey.cancelled': 'لم يُستخدم مفتاح المرور. حاول مرة أخرى، أو ادخل بكلمة السر.',
    'auth.passkey.unsupported': 'هذا المتصفح لا يدعم مفاتيح المرور. ادخل بكلمة السر.',
    'auth.passkey.failed': 'تعذّر استخدام مفتاح المرور على هذا الجهاز. حاول مرة أخرى، أو ادخل بكلمة السر.',
    'auth.invitation.usePasskey': 'القبول بمفتاح المرور بدل كلمة السر',
    'account.passkeys': 'مفاتيح المرور',
    'account.passkeys.hint': 'ادخل ببصمة الإصبع أو الوجه أو قفل شاشة الجهاز بدل كلمة السر. لكل جهاز مفتاحه.',
    'account.passkeys.none': 'لم تُضف أي مفتاح مرور بعد.',
    'account.passkeys.unnamed': 'مفتاح مرور',
    'account.passkeys.added': 'أُضيف في {date}',
    'account.passkeys.deviceName': 'اسم الجهاز',
    'account.passkeys.deviceName.hint': 'اختياري، لتعرفه في القائمة، مثل «هاتفي».',
    'account.passkeys.add': 'أضف مفتاح مرور على هذا الجهاز',
    'account.passkeys.saved': 'أُضيف مفتاح المرور. يمكنك الدخول به من الآن.',
    'account.passkeys.remove': 'إزالة',
    'account.passkeys.removed': 'أُزيل مفتاح المرور، ولم يعد يُدخل أحداً إلى حسابك.',
    'account.passkeys.cancelled': 'لم يُضف مفتاح المرور. حاول مرة أخرى حين تكون جاهزاً.',
    'account.passkeys.alreadyRegistered': 'على هذا الجهاز مفتاح مرور لحسابك بالفعل.',
    'account.passkeys.failed': 'تعذّر على هذا الجهاز إنشاء مفتاح مرور. حاول مرة أخرى.',
    'account.passkeys.unsupported': 'هذا المتصفح لا يدعم مفاتيح المرور. يمكنك إزالة المفاتيح المسجّلة هنا، وإضافة مفتاح من متصفح آخر.',

    // Notification channels
    'notifications.channel.inApp': 'داخل التطبيق',
    'notifications.channel.email': 'البريد',
    'notifications.channel.sms': 'رسالة نصية',
    'notifications.channel.push': 'على الهاتف',
    'notifications.channels.hint': 'إيقاف الإشعار داخل التطبيق يوقف نسخه الأخرى أيضاً: البريد والرسائل النصية والإشعارات على الهاتف.',

    // Push on this phone (Q68)
    'push.title': 'الإشعارات على هذا الهاتف',
    'push.hint': 'تصلك وإن كان التطبيق مغلقاً. لا تذكر اسم طفلك ولا اسم الحضانة، فمن يرى شاشة هاتفك لا يعرف عمّن هي.',
    'push.turnOn': 'تفعيل الإشعارات',
    'push.turnOff': 'إيقافها على هذا الهاتف',
    'push.on': 'الإشعارات مفعّلة على هذا الهاتف. تتوقف وحدها حين تخرج من حسابك.',
    'push.denied': 'منع المتصفح الإشعارات لهذا الموقع. اسمح بها من إعدادات المتصفح، ثم عُد إلى هنا.',
    'push.ios.title': 'لتصلك الإشعارات على آيفون',
    'push.ios.body': 'أضف التطبيق إلى الشاشة الرئيسية: اضغط زر المشاركة في سفاري، ثم «إضافة إلى الشاشة الرئيسية»، وافتحه من أيقونته (يتطلب iOS 16.4 أو أحدث).',
    'notifications.locked': 'تنبيه سلامة، لا يمكن إيقافه داخل التطبيق',
    'notifications.cell': '{type}، {channel}',

    // The invitation panel shown to whoever sent it
    'invitation.panel.title': 'أُنشئت الدعوة',
    'invitation.panel.smsSent': 'وصل الرابط إلى {phone} برسالة نصية.',
    'invitation.panel.smsFailed': 'تعذّر إرسال الرسالة النصية. انسخ الرابط وأرسله إلى المدعو بنفسك.',
    'invitation.panel.link': 'رابط الدعوة',
    'invitation.panel.copy': 'انسخ الرابط',
    'invitation.panel.expires': 'تنتهي صلاحيته في {date}.',
    'invitation.panel.once': 'يظهر هذا الرابط الآن فقط. من يفتحه يحتاج أيضاً إلى رمز يصل إلى هاتف المدعو.',
    'invitation.resend': 'أعد إرسال الدعوة',
    'invitation.email': 'البريد الإلكتروني (اختياري)',
    'invitation.email.hint': 'للإشعارات العادية فقط. تصل الدعوة برسالة نصية إلى الهاتف.',
  },

  en: {
    'auth.forgotPassword': 'Forgot password?',
    'auth.haveInvitation': 'I have an invitation',
    'auth.backToSignIn': 'Back to sign in',
    'legal.title': 'Privacy and terms',
    'legal.privacy': 'Privacy notice',
    'legal.terms': 'Terms of use',
    'legal.read.privacy': 'Read the privacy notice',
    'legal.read.terms': 'Read the terms of use',
    'legal.consent': 'I have read the privacy notice and the terms of use, and I accept them.',
    'legal.consent.required': 'Tick to accept the privacy notice and the terms of use to continue.',
    'legal.version': 'Version {version}',
    'legal.updated.title': 'The terms have been updated',
    'legal.updated.body': 'Read the privacy notice and the terms of use, then accept them to keep using the app.',
    'legal.accept': 'Accept and continue',
    'auth.entry.otherNursery.title': 'This is {nursery}’s link',
    'auth.entry.otherNursery.body': 'You are signed in to {current}. To go to {nursery}, sign out and sign in again.',
    'auth.entry.signOutAndContinue': 'Sign out and continue',
    'auth.entry.stay': 'Stay in {current}',

    'auth.invitation.paste.label': 'Invitation link',
    'auth.invitation.paste.hint': 'Paste the link you received by text message.',
    'auth.invitation.paste.invalid': 'This is not an invitation link. Copy the whole link from the text message.',
    'auth.invitation.continue': 'Continue',
    'auth.invitation.for': 'An invitation to join {place} as {name}.',
    'auth.invitation.forPlace': 'An invitation to join {place}.',
    'auth.invitation.platformTeam': 'the Daghfal team',
    'auth.invitation.expires': 'The invitation expires on {date}.',
    'auth.invitation.codeTo': 'We will text a confirmation code to {phone}.',
    'auth.invitation.existingAccount': 'This number already has a Daghfal account. Enter its password to join with it; no new password is needed.',
    'auth.invitation.currentPassword': 'Your account password',
    'auth.invitation.notYours': "This isn't my account",
    'auth.invitation.mine': 'This is my account',
    'auth.invitation.release.self': 'We will open a new account for you with this number. The old account keeps its records for its owner, and nobody can sign in to it from now on. Choose a password for your new account.',
    'auth.invitation.release.submit': 'Open my new account',
    'auth.invitation.release.centre': 'The account this number belongs to was used recently, so a text message alone cannot take the number from it. Ask whoever invited you to confirm who you are in person and release the old account, then open the invitation again.',
    'auth.invitation.release.platform': 'A text message alone cannot take this number from its account. Ask whoever invited you to contact the Daghfal team, who will confirm who you are and release the old account. Then open the invitation again.',
    'auth.code': 'Confirmation code',
    'auth.code.hint': 'Six digits, sent by text message.',
    'auth.code.required': 'Enter the six-digit code.',
    'auth.code.send': 'Send the code',
    'auth.code.sent': 'Code sent. It can take a minute to arrive.',
    'auth.code.failed': 'The text could not be sent right now. Try again shortly, or contact your setting.',
    'auth.code.resendIn.zero': 'You can ask for a new code now',
    'auth.code.resendIn.one': 'You can ask for a new code in 1 second',
    'auth.code.resendIn.other': 'You can ask for a new code in {count} seconds',
    'auth.code.resendInMinutes.other': 'You can ask for a new code in {count} minutes',

    'auth.reset.title': 'Reset your password',
    'auth.reset.hint': 'Enter the phone number you sign in with and we will text it a code.',
    'auth.reset.sent': 'If {phone} has an account, a code will arrive within a minute.',
    'auth.reset.newPassword': 'New password',
    'auth.reset.confirmPassword': 'Confirm new password',
    'auth.reset.submit': 'Save password',
    'auth.reset.done': 'Your password changed and you were signed out everywhere. Sign in with it now.',
    'auth.reset.held': 'To protect your account, the password will not change yet. You can finish after {date}: open this screen again and ask for a new code. If your account is open on another device, it shows a message about this request.',
    'auth.reset.awaitingCentre': 'The password will not change until your setting\'s administration confirms who you are in person. Visit them, then open this screen again and ask for a new code.',
    'auth.reset.awaitingPlatform': 'The password will not change until the Daghfal team confirms who you are. Ask your setting\'s administration to contact them, then open this screen again and ask for a new code.',
    'recovery.banner': 'Someone asked to reset your password with a code sent to your number. If this was not you, cancel it now.',
    'recovery.cancel': 'It wasn\'t me, cancel it',
    'auth.recoverLink.title': 'Set a new password',
    'auth.recoverLink.hint': 'The Daghfal team gave you this link. It works only once.',
    'auth.recoverLink.submit': 'Save password',
    'auth.recoverLink.done': 'Your new password is saved. Sign in with it now. If you are on the Daghfal team, add a passkey before you open the platform console.',
    'auth.recoverLink.missing': 'This link is incomplete. Open the whole link as the Daghfal team gave it to you.',
    'account.passkeys.currentPassword': 'Password, to confirm',
    'account.passkeys.currentPasswordHint': 'To protect your account, we ask for your password before adding a passkey.',

    'account.phoneConfirm.title': 'Confirm your new number',
    'account.phoneConfirm.hint': 'Press the button to use this number for your account. You will then be signed out everywhere.',
    'account.phoneConfirm.submit': 'Use this number',
    'account.phoneConfirm.done': 'Your number is now {phone}. Sign in with the new number.',
    'account.phoneConfirm.missing': 'The link is incomplete. Open the whole link from the text message.',

    'account.photo': 'Profile photo',
    'account.photo.hint': 'Your photo appears next to your name in every Daghfal app.',
    'child.photo': "Child's photo",
    'child.photo.hint': 'Helps staff recognise the child at arrival and pickup. Never shown to any other family.',
    'photo.add': 'Choose a photo',
    'photo.change': 'Change photo',
    'photo.remove': 'Remove photo',
    'photo.save': 'Save photo',
    'photo.preview': 'This is how the photo will look. Save it or choose another.',
    'photo.saved': 'Photo saved.',
    'photo.removed': 'Photo removed.',
    'photo.unreadable': 'This file could not be read as a photo. Choose a JPEG or PNG photo.',
    'photo.of': 'Photo of {name}',

    'logo.title': 'Nursery logo',
    'logo.hint': "Shown on the sign-in screen and when choosing a nursery. Anyone who knows the nursery's address can see it.",
    'logo.add': 'Choose a logo',
    'logo.change': 'Change logo',
    'logo.remove': 'Remove logo',
    'logo.save': 'Save logo',
    'logo.preview': 'This is how the logo will look. Save it or choose another.',
    'logo.saved': 'Logo saved.',
    'logo.removed': 'Logo removed.',
    'logo.unreadable': 'This file could not be read as an image. Choose a PNG or JPEG logo.',

    'account.title': 'My account',
    'account.profile': 'Profile',
    'account.name': 'Name',
    'account.name.hint': 'Your setting changes this if needed.',
    'account.phone': 'Phone number',
    'account.phone.hint': 'The number you sign in with.',
    'account.phone.unconfirmed': 'Not confirmed',
    'account.phone.new': 'New number',
    'account.phone.send': 'Send confirmation link',
    'account.phone.sent': 'We sent a link to {phone}. Open it within an hour to finish. If it does not arrive within a few minutes, check the number.',
    'account.phone.pending': 'A change to {phone} is waiting for its link to be opened, until {time}. Your current number keeps working until then.',
    'account.phone.cancel': 'Cancel the number change',
    'account.email': 'Email',
    'account.email.none': 'None',
    'account.email.hint': 'Optional, for invoices and ordinary notifications only. Never used to sign in.',
    'account.email.new': 'Email',
    'account.email.save': 'Save email',
    'account.email.remove': 'Remove email',
    'account.email.saved': 'Email saved.',
    'account.email.removed': 'Email removed.',
    'account.change': 'Change',
    'account.currentPassword': 'Current password',
    'account.currentPassword.hint': 'Asked for before any change, so nobody can change your account from a device you left open.',
    'account.password': 'Password',
    'account.password.new': 'New password',
    'account.password.confirm': 'Confirm new password',
    'account.password.save': 'Change password',
    'account.password.saved': 'Password changed. You were signed out of every other device.',
    'account.password.mismatch': 'The new passwords do not match.',
    'account.password.reset.hint': 'We text a code to your account\'s number. With it you choose a new password, without the old one.',
    'account.password.reset.sent': 'We texted a code to {phone}. It can take a minute to arrive.',
    'account.password.reset.submit': 'Save the new password',
    'account.sessions': 'Signed-in devices',
    'account.sessions.hint': 'Every device you signed in on and did not sign out of.',
    'account.sessions.since': 'Since {date}',
    'account.sessions.unknownDevice': 'Unknown device',
    'account.sessions.signOutAll': 'Sign out everywhere',
    'account.notifications': 'Notification channels',

    // Passkeys
    'auth.passkey.or': 'or',
    'auth.passkey.signIn': 'Sign in with a passkey',
    'auth.passkey.signIn.hint': 'With your fingerprint, face or device screen lock, and no password.',
    'auth.passkey.cancelled': 'The passkey was not used. Try again, or sign in with your password.',
    'auth.passkey.unsupported': 'This browser does not support passkeys. Sign in with your password.',
    'auth.passkey.failed': 'The passkey could not be used on this device. Try again, or sign in with your password.',
    'auth.invitation.usePasskey': 'Accept with a passkey instead of the password',
    'account.passkeys': 'Passkeys',
    'account.passkeys.hint': 'Sign in with your fingerprint, face or device screen lock instead of a password. Each device holds its own.',
    'account.passkeys.none': 'You have not added a passkey yet.',
    'account.passkeys.unnamed': 'Passkey',
    'account.passkeys.added': 'Added {date}',
    'account.passkeys.deviceName': 'Device name',
    'account.passkeys.deviceName.hint': 'Optional, so you recognise it in the list: "My phone", say.',
    'account.passkeys.add': 'Add a passkey on this device',
    'account.passkeys.saved': 'Passkey added. You can sign in with it from now on.',
    'account.passkeys.remove': 'Remove',
    'account.passkeys.removed': 'Passkey removed. It no longer signs anyone in to your account.',
    'account.passkeys.cancelled': 'No passkey was added. Try again when you are ready.',
    'account.passkeys.alreadyRegistered': 'This device already holds a passkey for your account.',
    'account.passkeys.failed': 'This device could not create a passkey. Try again.',
    'account.passkeys.unsupported': 'This browser does not support passkeys. You can remove registered ones here, and add one from another browser.',

    'notifications.channel.inApp': 'In the app',
    'notifications.channel.email': 'Email',
    'notifications.channel.sms': 'Text message',
    'notifications.channel.push': 'On the phone',
    'notifications.channels.hint': 'Turning a notification off in the app also stops its other copies: email, text messages and notifications on the phone.',

    'push.title': 'Notifications on this phone',
    'push.hint': "They reach you even when the app is closed. They never name your child or the nursery, so anyone who sees your screen cannot tell who they are about.",
    'push.turnOn': 'Turn on notifications',
    'push.turnOff': 'Turn off on this phone',
    'push.on': 'Notifications are on for this phone. They stop on their own when you sign out.',
    'push.denied': 'The browser blocked notifications for this site. Allow them in the browser settings, then come back here.',
    'push.ios.title': 'To get notifications on an iPhone',
    'push.ios.body': 'Add the app to your home screen: tap Share in Safari, then "Add to Home Screen", and open it from its icon (iOS 16.4 or later).',
    'notifications.locked': 'Safety alert; cannot be turned off in the app',
    'notifications.cell': '{type}: {channel}',

    'invitation.panel.title': 'Invitation created',
    'invitation.panel.smsSent': 'The link was texted to {phone}.',
    'invitation.panel.smsFailed': 'The text message could not be sent. Copy the link and send it to the invitee yourself.',
    'invitation.panel.link': 'Invitation link',
    'invitation.panel.copy': 'Copy link',
    'invitation.panel.expires': 'It expires on {date}.',
    'invitation.panel.once': 'This link is shown only now. Whoever opens it also needs a code sent to the invitee’s phone.',
    'invitation.resend': 'Resend invitation',
    'invitation.email': 'Email (optional)',
    'invitation.email.hint': 'For ordinary notifications only. The invitation goes to the phone by text message.',
  },
};
